/** @jsxImportSource react */
import { titleize } from '@common/helpers/strings';
import { getStateAbbr } from '@common/helpers/url';
import clsxm from '@common/lib/clsxm';
import { getTocLabel } from '@react/search/helpers';
import { usePrefetch } from '@react/services/api';
import { convertZeroToHundredToScaleOfTen } from '@react/shared/helpers/ratingBadge';
import { GeoGuide } from '@sly/core/api/types';
import { Agent } from '@sly/core/types';
import { SearchResourceNew } from '@sly/core/types';
import dayjs from 'dayjs';

import AgentInfo from './AgentInfo';
import CostGraphic from './CostGraphic';
import ReviewsSection from './CuratedReviews';
import MovesSection from './MovesSection';
import ScoreGraphic from './ScoreGraphic';
import CuratedSection from './Section/indext';
import { Summary } from './Summary';

interface CuratedProps {
  city: string;
  state: string;
  county?: string;
  toc: string;
  comms: SearchResourceNew[];
  geoGuide: GeoGuide;
  communitiesCount: number;
}

const Hr = ({ className }: { className?: string }) => (
  <hr
    className={clsxm(
      'my-6 mx-0 border-0 border-t md:my-xxl md:block',
      className
    )}
  />
);

export const CuratedIntro = ({
  city,
  state,
  county,
  toc,
  geoGuide,
  communitiesCount,
}: CuratedProps) => {
  const { requestInfo: requestInfoAgents } = usePrefetch('getAgents', {
    'filter[address]': `${city},${state}`,
    'page-size': 3,
  });

  const curated = geoGuide?.info?.curated;
  const dataPoints = geoGuide?.info?.curated?.dataPoints;

  const agent = requestInfoAgents?.normalized?.[0] as Agent;

  const { title } = getTitles(city, state, county, toc);

  return (
    !!geoGuide.guideContent?.curated &&
    !!dataPoints &&
    !!dataPoints.averageSeniorlyScore &&
    !!dataPoints.photos &&
    !!dataPoints.reviews &&
    !!dataPoints.prices && (
      <div className='-order-1 px-6 pt-12'>
        <div>
          <h2 className='font-t-xl mb-4'>{title}</h2>
          {agent && (
            <AgentInfo
              agent={agent}
              nameText={`Reviewed by ${agent?.info?.displayName}`}
              subText={`Last updated ${dayjs(curated?.lastUpdated).format(
                'MMMM DD, YYYY'
              )}`}
            />
          )}
          <Summary
            geoGuide={geoGuide}
            agents={requestInfoAgents?.normalized as Agent[]}
            communitiesCount={communitiesCount}
          />
        </div>
        <Hr />
      </div>
    )
  );
};

const getTitles = (
  city: string,
  state: string,
  county: string | undefined,
  toc: string
) => {
  const cityStr = titleize(city);

  let locationStr = '';
  if (county) {
    locationStr = `${titleize(county)}, ${getStateAbbr(state)}`;
  } else if (city) {
    locationStr = `${cityStr}, ${getStateAbbr(state)}`;
  } else {
    locationStr = `${titleize(state)}`;
  }

  const tocLabel = getTocLabel(toc);

  const title = `${tocLabel} in ${locationStr}`;

  const secTitle = `${tocLabel.toLowerCase()} in ${locationStr}`;

  return { title, secTitle, locationStr, cityStr, tocLabel };
};

const Curated = ({ city, state, county, toc, geoGuide }: CuratedProps) => {
  const { requestInfo } = usePrefetch('getSearchReviews', {
    include: ['searchResource', 'searchResource.gallery.images'],
    city: city,
    state: state,
  });

  const { title, secTitle, locationStr, cityStr, tocLabel } = getTitles(
    city,
    state,
    county,
    toc
  );

  const { requestInfo: requestInfoAgents } = usePrefetch('getAgents', {
    'filter[address]': `${city},${state}`,
    'page-size': 3,
  });

  const agent = requestInfoAgents?.normalized?.[0] as Agent;

  const curated = geoGuide?.info?.curated;
  const dataPoints = geoGuide?.info?.curated?.dataPoints;

  return (
    <div className='px-6 pt-12'>
      {!!geoGuide?.curatedCommunities?.topRated.length &&
        curated?.topRatedText && (
          <>
            <CuratedSection
              title={`Top rated ${secTitle}`}
              description={curated?.topRatedText}
              graphic={
                <div>
                  {!!dataPoints?.totalCommunities &&
                    dataPoints?.totalCommunities > 10 && (
                      <>
                        <div className='font-t-m-azo'>
                          {(
                            Math.round(
                              Number(dataPoints?.totalCommunities) / 10
                            ) * 10
                          ).toLocaleString()}
                          +
                        </div>
                        <div>Assisted living communities analyzed</div>
                      </>
                    )}
                  {!!dataPoints?.reviews && dataPoints?.reviews > 10 && (
                    <>
                      <div className='font-t-m-azo mt-4'>
                        {(
                          Math.round(Number(dataPoints?.reviews) / 10) * 10
                        ).toLocaleString()}
                        +
                      </div>
                      <div>Comprehensive reviews</div>
                    </>
                  )}
                  {!!dataPoints?.photos && dataPoints?.photos > 10 && (
                    <>
                      <div className='font-t-m-azo mt-4'>
                        {(
                          Math.round(Number(dataPoints?.photos) / 10) * 10
                        ).toLocaleString()}
                        +
                      </div>
                      <div>Community photographs</div>
                    </>
                  )}
                  {!!dataPoints?.prices && dataPoints?.prices > 10 && (
                    <>
                      <div className='font-t-m-azo mt-4'>
                        {(
                          Math.round(Number(dataPoints?.prices) / 10) * 10
                        ).toLocaleString()}
                        +
                      </div>
                      <div>Detailed pricing sources</div>
                    </>
                  )}
                </div>
              }
              communities={geoGuide?.curatedCommunities?.topRated}
            />
            <Hr />
          </>
        )}
      {!!geoGuide?.curatedCommunities?.bestValue.length &&
        curated?.bestValueText && (
          <>
            <CuratedSection
              title={`Best value ${secTitle}`}
              description={curated?.bestValueText}
              graphic={
                dataPoints && (
                  <CostGraphic
                    dataPoints={dataPoints}
                    city={locationStr}
                    careType={tocLabel.toLowerCase()}
                    avg={Math.round(
                      geoGuide?.curatedCommunities?.bestValue.reduce(
                        (total, comm) => comm.startingRate + total,
                        0
                      ) / geoGuide?.curatedCommunities?.bestValue?.length
                    )}
                  />
                )
              }
              communities={geoGuide?.curatedCommunities?.bestValue}
            />
            <Hr />
          </>
        )}
      {!!geoGuide?.curatedCommunities?.luxury?.length &&
        curated?.luxuryText && (
          <>
            <CuratedSection
              title={`Luxury ${secTitle}`}
              description={curated?.luxuryText}
              graphic={
                <ScoreGraphic
                  type='luxury'
                  total={geoGuide?.curatedCommunities?.luxury?.length}
                  score={convertZeroToHundredToScaleOfTen(
                    geoGuide?.curatedCommunities?.luxury.reduce(
                      (total, comm) => comm.slyScore + total,
                      0
                    ) / geoGuide?.curatedCommunities?.luxury?.length
                  )}
                />
              }
              communities={geoGuide?.curatedCommunities?.luxury}
            />
            <Hr />
          </>
        )}

      {!!geoGuide?.curatedCommunities?.agent?.length && curated?.agentText && (
        <>
          <CuratedSection
            title={`Advisor recommended ${secTitle}`}
            description={curated?.agentText}
            belowDescription={
              agent ? (
                <AgentInfo
                  agent={agent}
                  nameText={agent?.name}
                  subText={`${cityStr} Seniorly Advisor`}
                />
              ) : (
                <></>
              )
            }
            graphic={
              <ScoreGraphic
                type='advisor recommended'
                total={geoGuide?.curatedCommunities?.agent?.length}
                score={convertZeroToHundredToScaleOfTen(
                  geoGuide?.curatedCommunities?.agent.reduce(
                    (total, comm) => comm.slyScore + total,
                    0
                  ) / geoGuide?.curatedCommunities?.agent?.length
                )}
              />
            }
            communities={geoGuide?.curatedCommunities?.agent}
          />
          <Hr />
        </>
      )}
      {!!geoGuide?.curatedCommunities?.petFriendly?.length &&
        curated?.petFriendlyText && (
          <>
            <CuratedSection
              title={`Pet friendly ${secTitle}`}
              description={curated?.petFriendlyText}
              graphic={
                <ScoreGraphic
                  type='pet friendly'
                  total={geoGuide?.curatedCommunities?.petFriendly?.length}
                  score={convertZeroToHundredToScaleOfTen(
                    geoGuide?.curatedCommunities?.petFriendly.reduce(
                      (total, comm) => comm.slyScore + total,
                      0
                    ) / geoGuide?.curatedCommunities?.petFriendly?.length
                  )}
                />
              }
              communities={geoGuide?.curatedCommunities?.petFriendly}
            />
            <Hr />
          </>
        )}
      {!!geoGuide.guideContent?.moveIns?.length && (
        <>
          <MovesSection
            moveIns={geoGuide.guideContent.moveIns}
            title={`Search insights for the ${locationStr} area`}
            accordionTitle={`Seniorly verified move insights for the ${cityStr} area`}
          />
          <Hr />
        </>
      )}
      {!!requestInfo?.normalized?.length && (
        <>
          <ReviewsSection
            reviews={requestInfo.normalized}
            title={`Recent Reviews on ${title}`}
          />
          <Hr />
        </>
      )}
    </div>
  );
};

export default Curated;
